import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

/**
 * Shown instead of the app when required build-time config is missing.
 *
 * The point of this screen is that it is impossible to mistake for "the site is
 * down". A blank page under HTTP 200 looks identical to a network blip, a bad
 * deploy, a crashed pod and a browser problem — so it gets diagnosed as all four
 * before anyone checks the build. This says which variable is missing and where
 * it comes from, in the place the person is already looking.
 */
export default function ConfigError({ missing }: { missing: string[] }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={0}
        sx={{ maxWidth: 560, p: 4, border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
          Roamoir isn’t configured
        </Typography>
        <Typography sx={{ mb: 2, color: 'text.secondary' }}>
          This build is missing values it needed at build time, so it can’t reach
          its data. The site is running — the build was incomplete.
        </Typography>

        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Missing
        </Typography>
        <Box
          component="ul"
          sx={{ mt: 0.5, mb: 2.5, pl: 2.5, fontFamily: 'monospace', fontSize: 14 }}
        >
          {missing.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </Box>

        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          How to fix
        </Typography>
        <Typography sx={{ mt: 0.5, color: 'text.secondary', fontSize: 14 }}>
          Locally: copy <code>.env.example</code> to <code>.env.local</code>, fill
          in the Roamoir Supabase URL and anon key, then restart the dev server —
          CRA only reads these at startup.
          <br />
          <br />
          Deployed: set the matching repository secrets in GitHub Actions and
          rebuild. The Dockerfile refuses to build without them, so a deployed
          image should never reach this screen.
        </Typography>
      </Paper>
    </Box>
  );
}
